import React, { useCallback, useEffect, useRef, useState } from 'react';
import { faFileExcel } from '@fortawesome/pro-light-svg-icons';
import { YesNoDialog } from '@ygtechnologies/bygr-frontend-component-library';
import { AlertBanner, Button, ButtonBar, Card, Select } from '@ygtechnologies/rubus-ui';
import theme from './ProjectModules.module.scss';
import { BygrProject } from '@/types';
import { extractErrorMessage } from '@/utils/ajax';
import { useAsyncState } from '@/utils/hooks';
import { getCurrentUser, hasAccessLevel, UserInfoResponse } from '@/services/auth';
import {
    DocumentCategorisationOutcome,
    DocumentCategorisationPreview,
    DocumentCategorisationRun,
    DocumentCategorisationUndoPreview,
    getDocumentCategorisationReportUrl,
    getLatestDocumentCategorisation,
    previewDocumentCategorisation,
    previewUndoDocumentCategorisation,
    startDocumentCategorisation,
    undoDocumentCategorisation,
} from '@/services/projects';

const POLL_INTERVAL_MS = 5000;

const outcomeLabels: Record<DocumentCategorisationOutcome, string> = {
    Categorised: 'Categorised by AI',
    CopiedFromExisting: 'Copied from an existing copy',
    NoCategoryFound: 'No suitable category',
    NotPdf: 'File type not supported',
    NoText: 'Could not be read',
    AiError: 'Failed',
};

const maxCategoryOptions: { value: number; label: string }[] = [
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
];

const formatUsd = (value: number) => `$${value.toFixed(2)}`;

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '');

const isInProgress = (run: DocumentCategorisationRun | null) => run?.status === 'Queued' || run?.status === 'Running';

const PreviewSummary: React.FC<{ preview: DocumentCategorisationPreview }> = ({ preview }) => {
    if (!preview.hasExplicitCategoryTable) {
        return (
            <AlertBanner type="danger">
                <b>No document category table is selected for this project.</b> Without one, documents fall back to an
                outdated table. Select a document category table first, then preview again.
            </AlertBanner>
        );
    }

    return (
        <AlertBanner type={preview.canStart ? (preview.isDeprecatedCategoryTable ? 'warning' : 'info') : 'warning'}>
            {preview.isDeprecatedCategoryTable && (
                <p className={theme.transferResultNote}>
                    <b>The selected category table is marked as outdated.</b> Check that this is the table the customer
                    wants.
                </p>
            )}
            {!preview.canStart && preview.cannotStartReason && <b>{preview.cannotStartReason}</b>}
            <dl className={theme.transferResult}>
                <dt>Category table</dt>
                <dd>
                    {preview.categoryTableName ?? 'Unknown'} ({preview.categoryCount} categories)
                </dd>
                <dt>Documents in project</dt>
                <dd>{preview.documentCount}</dd>
                <dt>Without categories</dt>
                <dd>{preview.uncategorisedDocumentCount}</dd>
                <dt>Distinct files to process</dt>
                <dd>{preview.groupsToProcess}</dd>
                <dt>Analysed by AI</dt>
                <dd>{preview.groupsToAnalyse}</dd>
                <dt>Copied from a categorised copy</dt>
                <dd>{preview.groupsToCopyFromExisting}</dd>
                <dt>Skipped, file type not supported</dt>
                <dd>{preview.nonPdfGroups}</dd>
                <dt>Estimated cost</dt>
                <dd>about {formatUsd(preview.estimatedCostUsd)}</dd>
            </dl>
            <p className={theme.transferResultNote}>Nothing has been written yet.</p>
        </AlertBanner>
    );
};

interface RunSummaryProps {
    run: DocumentCategorisationRun;
    canUndo: boolean;
    isBusy: boolean;
    onUndo(): void;
}

const RunSummary: React.FC<RunSummaryProps> = ({ run, canUndo, isBusy, onUndo }) => {
    const reportUrl = getDocumentCategorisationReportUrl(run.projectId, run.id);
    const inProgress = isInProgress(run);
    const type = inProgress ? 'info' : run.status === 'Completed' ? 'success' : 'danger';

    return (
        <AlertBanner type={type}>
            {run.status === 'Queued' && <b>Queued. It starts when AM has finished any run ahead of it.</b>}
            {run.status === 'Running' && (
                <b>
                    Running: {run.groupsProcessed} of {run.groupCount} files processed.
                </b>
            )}
            {run.status === 'Completed' && <b>Finished. {run.documentsUpdated} documents received categories.</b>}
            {(run.status === 'Failed' || run.status === 'Interrupted') && (
                <b>
                    {run.status === 'Failed' ? 'The run failed' : 'The run was interrupted'} after {run.groupsProcessed} of{' '}
                    {run.groupCount} files.
                </b>
            )}
            {run.errorMessage && <p className={theme.transferResultNote}>{run.errorMessage}</p>}
            {run.undoneAt && (
                <p className={theme.transferResultNote}>
                    <b>Undone</b> {formatDate(run.undoneAt)}
                    {run.undoneBy ? ` by ${run.undoneBy}` : ''}: {run.categoriesRemoved ?? 0} categories removed.
                </p>
            )}
            {run.outcomes.length > 0 && (
                <dl className={theme.transferResult}>
                    {run.outcomes.map((x) => (
                        <React.Fragment key={x.outcome}>
                            <dt>{outcomeLabels[x.outcome] ?? x.outcome}</dt>
                            <dd>
                                {x.files} files, {x.documents} documents
                            </dd>
                        </React.Fragment>
                    ))}
                    <dt>Cost so far</dt>
                    <dd>about {formatUsd(run.costUsd)}</dd>
                </dl>
            )}
            {!inProgress && (
                <ButtonBar margins="top">
                    <Button as="a" size="sm" variant="tertiary" icon={faFileExcel} href={reportUrl}>
                        Download report
                    </Button>
                    {canUndo && !run.undoneAt && (
                        <Button size="sm" variant="tertiary" onClick={onUndo} disabled={isBusy}>
                            Undo this run
                        </Button>
                    )}
                </ButtonBar>
            )}
        </AlertBanner>
    );
};

interface DocumentCategorisationToolProps {
    project: BygrProject;
}

const DocumentCategorisationTool: React.FC<DocumentCategorisationToolProps> = ({ project }) => {
    const [preview, setPreview] = useState<DocumentCategorisationPreview | null>(null);
    const [run, setRun] = useState<DocumentCategorisationRun | null>(null);
    const [undoPreview, setUndoPreview] = useState<DocumentCategorisationUndoPreview | null>(null);
    const [maxCategories, setMaxCategories] = useState(3);
    const [isBusy, setIsBusy] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [user] = useAsyncState<UserInfoResponse | null>(getCurrentUser, [], null);
    const canRun = user != null && hasAccessLevel(user.accessLevel, 'Advanced');

    // Responses can arrive after the user has moved to another project; those must not land on this one.
    const currentProjectId = useRef(project.id);
    currentProjectId.current = project.id;
    const isCurrent = (projectId: number) => currentProjectId.current === projectId;

    const loadLatest = useCallback(async () => {
        const projectId = project.id;
        try {
            const latest: DocumentCategorisationRun | null = await getLatestDocumentCategorisation(projectId);
            if (isCurrent(projectId)) setRun(latest);
        } catch {
            // Keep showing the last known state; the next poll or a reload will catch up.
        }
    }, [project.id]);

    // ProjectDetails re-renders rather than remounts when the route's projectId changes.
    useEffect(() => {
        setPreview(null);
        setRun(null);
        setUndoPreview(null);
        setError(null);
        loadLatest();
    }, [loadLatest]);

    const inProgress = isInProgress(run);
    useEffect(() => {
        if (!inProgress) return;
        const timer = window.setInterval(loadLatest, POLL_INTERVAL_MS);
        return () => window.clearInterval(timer);
    }, [inProgress, loadLatest]);

    const runAction = async (action: (projectId: number) => Promise<void>, fallbackError: string) => {
        const projectId = project.id;
        setIsBusy(true);
        setError(null);
        try {
            await action(projectId);
        } catch (e) {
            const message: string | null = await extractErrorMessage(e);
            if (isCurrent(projectId)) setError(message ?? fallbackError);
        } finally {
            setIsBusy(false);
        }
    };

    const onPreview = () =>
        runAction(async (projectId) => {
            const result: DocumentCategorisationPreview = await previewDocumentCategorisation(projectId);
            if (!isCurrent(projectId)) return;
            setPreview(result);
            if (result.activeRun) setRun(result.activeRun);
        }, 'The preview failed. Check that the AM backend is reachable.');

    const onStart = () =>
        runAction(async (projectId) => {
            setShowConfirm(false);
            setPreview(null);
            const started: DocumentCategorisationRun = await startDocumentCategorisation(projectId, maxCategories);
            if (isCurrent(projectId)) setRun(started);
        }, 'Starting the run failed. Nothing was written.');

    const onUndoRequested = () =>
        runAction(async (projectId) => {
            if (!run) return;
            const counts: DocumentCategorisationUndoPreview = await previewUndoDocumentCategorisation(projectId, run.id);
            if (isCurrent(projectId)) setUndoPreview(counts);
        }, 'Could not check what undoing the run would remove.');

    const onUndo = () =>
        runAction(async (projectId) => {
            if (!undoPreview) return;
            const runId = undoPreview.runId;
            setUndoPreview(null);
            const undone: DocumentCategorisationRun = await undoDocumentCategorisation(projectId, runId);
            if (isCurrent(projectId)) setRun(undone);
        }, 'Undoing the run failed.');

    return (
        <Card variant="flat">
            <h4 className={theme.cardTitle}>AI-categorise documents</h4>
            <p>
                Gives every document in this project that has no category one or more categories from the project&apos;s
                document category table, chosen by AI from the document&apos;s text, or from the picture itself for images
                and the first page of scanned PDFs. Identical files uploaded several times are analysed once and every copy
                gets the same categories. Documents that already have categories are not changed. Afterwards, download the
                report to see which files could not be categorised.
            </p>
            {canRun && (
                <label>
                    Max categories per document
                    <Select
                        inputSize="md"
                        options={maxCategoryOptions}
                        value={maxCategories}
                        onChange={setMaxCategories}
                        disabled={isBusy || inProgress}
                    />
                </label>
            )}
            <ButtonBar margins="both">
                <Button variant="tertiary" size="md" onClick={onPreview} disabled={isBusy}>
                    Preview
                </Button>
                {canRun && (
                    <Button
                        variant="primary"
                        size="md"
                        onClick={() => setShowConfirm(true)}
                        disabled={isBusy || inProgress || !preview?.canStart}>
                        Start
                    </Button>
                )}
            </ButtonBar>
            {user != null && !canRun && (
                <p className={theme.transferResultNote}>Starting or undoing a run needs Advanced access in MacGyver.</p>
            )}

            {preview && <PreviewSummary preview={preview} />}
            {run && <RunSummary run={run} canUndo={canRun} isBusy={isBusy} onUndo={onUndoRequested} />}
            {error && <AlertBanner type="danger">{error}</AlertBanner>}

            <YesNoDialog
                title="Start AI categorisation?"
                show={showConfirm}
                onClose={() => setShowConfirm(false)}
                onAccept={onStart}
                body={`This analyses ${preview?.groupsToAnalyse ?? 0} files with AI (about ${formatUsd(
                    preview?.estimatedCostUsd ?? 0
                )}) and writes categories to up to ${preview?.uncategorisedDocumentCount ?? 0} documents in ${
                    project.name
                }. It runs in the background and can take a while.`}
            />
            <YesNoDialog
                title="Undo this categorisation run?"
                show={undoPreview != null}
                onClose={() => setUndoPreview(null)}
                onAccept={onUndo}
                body={
                    undoPreview == null
                        ? ''
                        : `This removes ${undoPreview.categoriesToRemove} categories the run gave ${undoPreview.documentsAffected} documents in ${project.name}.` +
                          (undoPreview.documentsChangedSince > 0
                              ? ` ${undoPreview.documentsChangedSince} documents whose categories someone has edited since the run keep what they have.`
                              : '')
                }
            />
        </Card>
    );
};

export default DocumentCategorisationTool;
